import { Injectable } from '@angular/core';
import { HttpInterceptor, HttpRequest, HttpHandler, HttpEvent, HttpParams } from '@angular/common/http';
import { Observable, from } from 'rxjs';
import { switchMap } from 'rxjs/operators';

@Injectable()
export class EncryptionInterceptor implements HttpInterceptor {
  // List of keys to encrypt in query parameters
  private readonly keysToEncrypt = [
    'id', 'Id', 'universityId', 
    'courseId', 'userId', 'roleId', 'smtpsId', 'migrationId',
    'applicationId', 'messageId', 'mailCommunicationId', 'userMailCalendarId',
    'bankDetailsId', 'voucherId', 'agentsId', 'agentId', 'universityEntryRequirementId', 'universityFilterId',
    'universityAffiliateId', 'universityContractId', 'emailTemplatesId', 'primaryStatusId', 'agentCommunicationStatusId',
    'studentPreviousQualificationId', 'applicationCommentsId', 'username', 'jobRole', 'storeId', 'manufactureId', 'camsId',
    'universityCountryId', 'destinationCountryId', 'studentCountryId', 'intakeName', 'promotion', 'university'
  ];

  // Secret key
  private readonly secret = 'crizacBJJF2w5hSgE6L05Lcencode';
  
  // Cached crypto key
  private cryptoKey: CryptoKey | null = null;
  
  constructor() {
    // Initialize the crypto key when the service starts
    this.initCryptoKey();
  }
  
  // Initialize the CryptoKey object
  private async initCryptoKey(): Promise<void> {
    if (this.cryptoKey) return;
    
    // Create a key from the secret
    const encoder = new TextEncoder();
    const keyData = await crypto.subtle.digest(
      'SHA-256',
      encoder.encode(this.secret)
    );
    
    this.cryptoKey = await crypto.subtle.importKey(
      'raw',
      keyData,
      { name: 'AES-CBC' },
      false,
      ['encrypt', 'decrypt']
    );
  }
  // v2
  /* this.http.get(url, { params: new HttpParams().set('agentId', '17454798049706')}) for future implementation */
  // intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
  //   console.log('Encryption Interceptor');
  //   console.log(request.params);
    
  //   // If there are no query parameters, just pass through
  //   if (!request.params || request.params.keys().length === 0) {
  //     return next.handle(request);
  //   }
    
  //   // Process the query parameters
  //   return from(this.processQueryParams(request.params)).pipe(
  //     switchMap(encryptedParams => {
  //       // Clone the request with the encrypted query parameters
  //       const encryptedRequest = request.clone({
  //         params: encryptedParams
  //       });
  //       console.log(encryptedParams);
        
        
  //       return next.handle(encryptedRequest);
  //     })
  //   );
  // }

  // intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
  //   const url = new URL(request.urlWithParams, window.location.origin);
  //   const searchParams = new URLSearchParams(url.search);
  
  //   const keysToEncrypt = this.keysToEncrypt.map(k => k.toLowerCase());
  
  //   // Check if any params match your list and encrypt them
  //   const updatedSearchParams = new URLSearchParams();
  
  //   const encryptPromises: Promise<void>[] = [];
  
  //   searchParams.forEach((value, key) => {
  //     if (keysToEncrypt.includes(key.toLowerCase()) && value !== '') {
  //       encryptPromises.push(
  //         this.encrypt(value).then(encrypted => {
  //           updatedSearchParams.set(key, encrypted);
  //         })
  //       );
  //     } else {
  //       updatedSearchParams.set(key, value);
  //     }
  //   });
  
  //   return from(Promise.all(encryptPromises)).pipe(
  //     switchMap(() => {
  //       const updatedUrl = url.origin + url.pathname + '?' + updatedSearchParams.toString();
  
  //       const cloned = request.clone({ url: updatedUrl });
  //       return next.handle(cloned);
  //     })
  //   );
  // }

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const url = new URL(request.urlWithParams, window.location.origin);
    const searchParams = new URLSearchParams(url.search);
  
    const keysToEncrypt = this.keysToEncrypt.map(k => k.toLowerCase());
  
    // Check if any params match your list and encrypt them
    const paramPairs: string[] = [];
  
    const encryptPromises: Promise<void>[] = [];
  
    searchParams.forEach((value, key) => {
      if (keysToEncrypt.includes(key.toLowerCase()) && value !== '') {
        encryptPromises.push(
          this.encrypt(value).then(encrypted => {
            // Store key-value pair without encoding
            paramPairs.push(`${key}=${encrypted}`);
          })
        );
      } else {
        // For non-encrypted values, let URLSearchParams handle encoding
        paramPairs.push(`${key}=${value}`);
      }
    });
  
    return from(Promise.all(encryptPromises)).pipe(
      switchMap(() => {
        // Manually construct the query string
        const queryString = paramPairs.join('&');
        const updatedUrl = url.origin + url.pathname + (queryString ? '?' + queryString : '');
  
        const cloned = request.clone({ url: updatedUrl });
        return next.handle(cloned);
      })
    );
  }
  
  
  
  // Make sure we have a crypto key
  private async ensureCryptoKey(): Promise<void> {
    if (!this.cryptoKey) {
      await this.initCryptoKey();
    }
  }
  
  /**
   * Process query parameters and encrypt specified keys
   */
  private async processQueryParams(params: HttpParams): Promise<HttpParams> {
    // Ensure we have a crypto key
    await this.ensureCryptoKey();
    
    let encryptedParams = new HttpParams();
    
    // Get all parameter keys
    const paramKeys = params.keys();
    
    // Process each parameter
    for (const key of paramKeys) {
      const value = params.get(key);
      
      // If the key should be encrypted and has a value
      if (this.keysToEncrypt.includes(key) && value !== null && value !== '') {
        // Encrypt the value
        const encryptedValue = await this.encrypt(value);
        encryptedParams = encryptedParams.set(key, encryptedValue);
      } else {
        // Pass through unencrypted
        encryptedParams = encryptedParams.set(key, value || '');
      }
    }
    
    return encryptedParams;
  }
  
  /**
   * Encrypt a string value using AES-CBC
   */
  private async encrypt(text: string): Promise<string> {
    try {
      // Ensure we have a crypto key
      await this.ensureCryptoKey();
      
      // Create a static IV (16 bytes of zeros) for compatibility
      const iv = new Uint8Array(16);
      
      // Encode the text
      const encoder = new TextEncoder();
      const data = encoder.encode(text);
      
      // Encrypt the data
      const encryptedBuffer = await crypto.subtle.encrypt(
        {
          name: 'AES-CBC',
          iv
        },
        this.cryptoKey!,
        data
      );
      
      // Convert to base64
      const encryptedArray = Array.from(new Uint8Array(encryptedBuffer));
      const base64 = btoa(String.fromCharCode.apply(null, encryptedArray));
      
      // URL encode for safety
      return encodeURIComponent(base64);
      // return base64;
    } catch (error) {
      console.error('Encryption error:', error);
      return text; // Return original on error
    }
  }
}